import {
  OwnUpFillButtonSecondary,
  OwnUpTextInput,
  PropsWithTheme
} from '@rategravity/own-up-component-library';
import { ChevronDownIcon } from '@rategravity/own-up-component-library/icon-library/system-icons/standard-icons/chevron-down';
import { ChevronUpIcon } from '@rategravity/own-up-component-library/icon-library/system-icons/standard-icons/chevron-up';
import { DollarIcon } from '@rategravity/own-up-component-library/icon-library/system-icons/standard-icons/dollar';

import React, { useCallback, useState } from 'react';

import styled from 'styled-components';
import { RefreshIcon } from '../../../../images/icons/refresh-icon';
import { LabelWithIcon } from '../../common/info-label';
import { WidgetTextInput } from '../../common/WidgetTextInput';
import { useMonthlyPayment } from '../data/dataProvider';
import { MonthlyPaymentDataActionsTypes } from '../types';

const AdvancedOptionsWrapper = styled.div`
  margin-top: ${({ theme }: PropsWithTheme<{}>) => theme.spacing(4)}px;
`;
export const FullWidthSecondaryButton = styled(OwnUpFillButtonSecondary)`
  width: 100%;
`;
const RightAlignedOptionWrapper = styled.div`
  margin-top: ${({ theme }: PropsWithTheme<{}>) => theme.spacing(3)}px;
  text-align: right;
`;

type AdvancedInputs = {
  propertyTaxAnnual: number;
  homeownersInsuranceMonthly: number;
  hoaFeesMonthly: number;
};

type InputEvent = {
  inputTitle: keyof AdvancedInputs;
  value: number;
};

const actionMapping = {
  propertyTaxAnnual: MonthlyPaymentDataActionsTypes.SET_PROPERTY_TAX_ANNUAL,
  homeownersInsuranceMonthly: MonthlyPaymentDataActionsTypes.SET_HOMEOWNTERS_INSURANCE_MONTHLY,
  hoaFeesMonthly: MonthlyPaymentDataActionsTypes.SET_HOA_FEES_MONTHLY
} as const;

export const AdvancedInputsComponent = ({
  inputs,
  updateInputs,
  resetToDefaults
}: {
  inputs: AdvancedInputs;
  updateInputs: (inputs: InputEvent) => void;
  resetToDefaults: () => void;
}) => {
  const { propertyTaxAnnual, homeownersInsuranceMonthly, hoaFeesMonthly } = inputs;

  const [inputsVisible, setInputsVisible] = useState(false);
  const buttonIcon = () => (inputsVisible ? <ChevronUpIcon /> : <ChevronDownIcon />);

  const changeCalculatorVisibility = () => {
    setInputsVisible(!inputsVisible);
  };
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <AdvancedOptionsWrapper>
      <FullWidthSecondaryButton
        icon={buttonIcon}
        iconPosition="right"
        onClick={changeCalculatorVisibility}
      >
        Advanced options
      </FullWidthSecondaryButton>
      {inputsVisible && (
        <>
          <WidgetTextInput
            customInput={OwnUpTextInput}
            label={<LabelWithIcon label="Property tax annual" />}
            value={propertyTaxAnnual}
            labelPosition="outer"
            $leadingIcon={DollarIcon()}
            decimalScale={0}
            allowNegative={false}
            thousandSeparator
            onChange={(e) => {
              updateInputs({
                inputTitle: 'propertyTaxAnnual',
                value: Number(e.target.value.replace(/[^0-9.-]/g, ''))
              });
            }}
          />
          <WidgetTextInput
            customInput={OwnUpTextInput}
            label={<LabelWithIcon label="Homeowners insurance monthly" />}
            value={homeownersInsuranceMonthly}
            labelPosition="outer"
            $leadingIcon={DollarIcon()}
            decimalScale={3}
            allowNegative={false}
            thousandSeparator
            onChange={(e) => {
              updateInputs({
                inputTitle: 'homeownersInsuranceMonthly',
                value: Number(e.target.value.replace(/[^0-9.-]/g, ''))
              });
            }}
          />
          <WidgetTextInput
            customInput={OwnUpTextInput}
            label={<LabelWithIcon label="Hoa fees monthly" />}
            value={hoaFeesMonthly}
            labelPosition="outer"
            $leadingIcon={DollarIcon()}
            decimalScale={3}
            allowNegative={false}
            thousandSeparator
            onChange={(e) => {
              updateInputs({
                inputTitle: 'hoaFeesMonthly',
                value: Number(e.target.value.replace(/[^0-9.-]/g, ''))
              });
            }}
          />

          <RightAlignedOptionWrapper>
            <OwnUpFillButtonSecondary icon={<RefreshIcon />} onClick={resetToDefaults}>
              Return to defaults
            </OwnUpFillButtonSecondary>
          </RightAlignedOptionWrapper>
        </>
      )}
    </AdvancedOptionsWrapper>
  );
};

export const AdvancedInputs = () => {
  const {
    state: { propertyTaxAnnual, homeownersInsuranceMonthly, hoaFeesMonthly },
    dispatch
  } = useMonthlyPayment();
  const updateInputs = useCallback(
    (inputs: InputEvent) => {
      const actionType = actionMapping[inputs.inputTitle];
      dispatch({ type: actionType, payload: inputs.value });
    },
    [dispatch]
  );
  const resetToDefaults = () => {
    dispatch({ type: MonthlyPaymentDataActionsTypes.SET_DEFAULTS });
  };

  return (
    <AdvancedInputsComponent
      inputs={{ propertyTaxAnnual, homeownersInsuranceMonthly, hoaFeesMonthly }}
      updateInputs={updateInputs}
      resetToDefaults={resetToDefaults}
    />
  );
};
